
import {Vue, Component, Watch} from 'vue-property-decorator';
import Template from "@/views/system-management/template/index.vue";
import Pagination from "@/components/Pagination/index.vue";
import PoDeliveryTracking from "./components/PoDeliveryTracking.vue";
import {
  queryCustomerByShipmentTrack,
  queryPIShipmentStatusCountOfCustomer,
  queryShipmentTrack
} from "@/api/proforma";

interface DeliveryTrackingRow {
  id: string;
  code: string; // 销售订单号
  status: { name: string; desc: string }; // 采购订单状态
  createdAt: string;
  firstOrderDate: string; // 首次下单时间
  leadTime: number; // 交货期(天)
  shipmentDate: string; // 约定交期
  remainingShipmentDays: number; // 剩余交货天数
  overdueDays: number; // 超时天数
  shipmentAmount: number; // 交货金额
  orderAmount: number; // 订单金额
  shipmentQty: number; // 交货数量
  orderQty: number; // 下单数量
  amountRate: number; // 金额交货率
  quantityRate: number; // 数量交货率
  overdueAndNotCompletePoCount: number;
  currency: string; // 币种
}

interface CustomerSummary {
  customerId: string;
  customerCode: string;
  customerAbbr: string;
  seller: string;
  sellerAssistant: string;
  overduePiCount: number;
}

interface ListQueryModel {
  codeKeyword: string;
  states: string[];
  lastStates: string[];
  createdOrOverdueTimeDesc: boolean;
}

@Component({
  name: "piDeliveryTracking",
  components: {
    Template,
    Pagination,
    PoDeliveryTracking,
  },
})
export default class extends Vue {
  private customerList: CustomerSummary[] = [];
  private customerListLoading = false;
  public customerPageNumber = 1;
  public customerPageSize = 20;
  private customerPageTotal = 0;
  public activeCustomerId = "";
  private activeCustomer: CustomerSummary = {
    customerId: "",
    customerCode: "",
    customerAbbr: "",
    seller: "",
    sellerAssistant: "",
    overduePiCount: 0,
  };
  public customerKeyword = "";
  private tableData: DeliveryTrackingRow[] = [];
  private tableDataLoading = false;
  private pageNumber = 1;
  private pageSize = 20;
  private pageTotal = 0;
  public listQuery: ListQueryModel = {
    codeKeyword: "",
    states: ['UNSHIPPED', 'PARTIAL_SHIPMENT'],
    lastStates: ['UNSHIPPED', 'PARTIAL_SHIPMENT'],
    createdOrOverdueTimeDesc: true,
  };
  public statusFilterOptions = [
    {name: "UNSHIPPED", desc: "未发货"},
    {name: "PARTIAL_SHIPMENT", desc: "部分发货"},
    {name: "COMPLETED", desc: "已完成"},
  ];
  private statusOrderCount = [];

  get currentCustomerCode(): string {
    return this.activeCustomer
        ? this.activeCustomer.customerCode ?
            this.activeCustomer.customerCode : "--"
        : "--";
  }

  get currentCustomerAbbr(): string {
    return this.activeCustomer
        ? this.activeCustomer.customerAbbr
            ? this.activeCustomer.customerAbbr : "--"
        : "--";
  }

  get currentSeller(): string {
    return this.activeCustomer
        ? this.activeCustomer.seller
            ? this.activeCustomer.seller : "--"
        : "--";
  }

  get currentSellerAssistant(): string {
    return this.activeCustomer
        ? this.activeCustomer.sellerAssistant
            ? this.activeCustomer.sellerAssistant : "--"
        : "--";
  }

  @Watch('activeCustomerId')
  private onActiveCustomerIdChange() {
    this.getShipmentTrackingData();
  }

  async created() {
    await this.getCustomerList();
    // this.activeCustomerId = this.customerList.length ? this.customerList[0].customerId : "";
    // this.ensureActiveCustomerOnCurrentPage();
  }

  private async getCustomerList() {
    this.customerListLoading = true;
    try {
      const params = {
        keyword: this.customerKeyword,
        pageNumber: this.customerPageNumber,
        pageSize: this.customerPageSize
      }
      const customerRes = await queryCustomerByShipmentTrack(params) as any;
      this.customerPageTotal = Math.ceil(Number(customerRes.total) / this.pageSize);
      this.customerList = customerRes.list;
    } finally {
      this.customerListLoading = false;
    }
  }

  private async handleCustomerKeywordChange() {
    this.customerPageNumber = 1;
    await this.getCustomerList();
    // this.ensureActiveCustomerOnCurrentPage();
  }

  private async getShipmentTrackingData() {
    this.tableDataLoading = true;
    try {
      const params = {
        customerId: this.activeCustomerId,
        ...this.listQuery,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize
      }
      const res = await queryShipmentTrack(params) as any;
      this.tableData = res.list.map(item => {
        return {
          amountRate: item.orderAmount !== 0 ? Math.floor((item.shipmentAmount / item.orderAmount) * 100) : 0,
          quantityRate: item.orderQty !== 0 ? Math.floor((item.shipmentQty / item.orderQty) * 100) : 0,
          ...item,
        }
      });
      this.pageTotal = Number(res.total);
      this.statusOrderCount = await queryPIShipmentStatusCountOfCustomer(
          {customerId: this.activeCustomerId}
      ) as any;
    } finally {
      this.tableDataLoading = false;
    }
  }

  private statusFilterShowCount(state) {
    const find = this.statusOrderCount.find(item => item.shipmentStatus.name === state.name);
    if (find) {
      return `${state.desc}(${find.count})`;
    }
    return state.desc;
  }

  public handleStatusFilterChange(): void {
    if (this.listQuery.states.length === 0) {
      this.listQuery.states = this.listQuery.lastStates;
      return;
    }
    this.listQuery.lastStates = this.listQuery.states;
    this.pageNumber = 1;
    this.getShipmentTrackingData();
  }

  public setSortType(type: boolean): void {
    this.listQuery.createdOrOverdueTimeDesc = type;
    this.pageNumber = 1;
    this.getShipmentTrackingData();
  }

  public handleCustomerChange(customerId: string): void {
    this.activeCustomerId = customerId;
    this.activeCustomer = this.customerList.find((item) => item.customerId === this.activeCustomerId);
    this.pageNumber = 1;
  }

  private async handleCustomerPrevPage() {
    if (this.customerPageNumber <= 1) {
      return;
    }
    this.customerPageNumber -= 1;
    await this.getCustomerList();
    // this.ensureActiveCustomerOnCurrentPage();
  }

  private async handleCustomerNextPage() {
    if (this.customerPageNumber >= this.customerPageTotal) {
      return;
    }
    this.customerPageNumber += 1;
    await this.getCustomerList();
    // this.ensureActiveCustomerOnCurrentPage();
  }

  public ensureActiveCustomerOnCurrentPage(): void {
    const currentPageList = this.customerList;
    if (!currentPageList.length) {
      return;
    }
    const activeInCurrentPage = currentPageList.some(
        (item) => item.customerId === this.activeCustomerId
    );
    if (!activeInCurrentPage) {
      this.activeCustomerId = currentPageList[0].customerId;
      this.activeCustomer = currentPageList[0];
      this.pageNumber = 1;
    }
  }

  public statusTagType(status: string): string {
    if (status === "COMPLETED") {
      return "success";
    }
    if (status === "PARTIAL_SHIPMENT") {
      return "warning";
    }
    if (status === "UNSHIPPED") {
      return "info";
    }
    return "";
  }

  public progressColor(value: number): string {
    if (value >= 100) {
      return "#67c23a";
    }
    if (value >= 60) {
      return "#e6a23c";
    }
    return "#f56c6c";
  }

  public getRemainingDays(row: DeliveryTrackingRow): number | null {
    if (!row.shipmentDate) {
      return null;
    }
    if (row.status.name === "COMPLETED") {
      return null;
    }
    const days = row.remainingShipmentDays;
    if (days === null) {
      return null;
    }
    return days > 0 ? days : 0;
  }

  public isOverdue(row: DeliveryTrackingRow) {
    const days = this.getOverdueDaysText(row);
    return days > 0;
  }

  private getShipmentDays(row: DeliveryTrackingRow) {
    if (row.status.name !== "COMPLETED") {
      return row.leadTime ? row.leadTime : "--";
    } else {
      return row.leadTime ? row.leadTime : "error";
    }
  }

  private getFirstOrderDate(row: DeliveryTrackingRow) {
    if (row.status.name !== "COMPLETED") {
      return row.leadTime
          ? row.firstOrderDate ? row.firstOrderDate : "--"
          : "--";
    } else {
      return row.leadTime
          ? row.firstOrderDate ? row.firstOrderDate : "error"
          : "error";
    }
  }

  private getShipmentDate(row: DeliveryTrackingRow) {
    if (row.status.name !== "COMPLETED") {
      return row.leadTime
          ? row.firstOrderDate
              ? row.shipmentDate ? row.shipmentDate : "--"
              : "--"
          : "--";
    } else {
      return row.leadTime
          ? row.firstOrderDate
              ? row.shipmentDate ? row.shipmentDate : "error"
              : "error"
          : "error";
    }
  }

  public getRemainingDaysText(row: DeliveryTrackingRow) {
    if (row.status.name !== "COMPLETED") {
      if (row.leadTime) {
        if (row.firstOrderDate) {
          if (row.shipmentDate) {
            return row.remainingShipmentDays != null
                ? row.remainingShipmentDays >= 0
                    ? row.remainingShipmentDays : ""
                : "error";
          } else {
            return "--";
          }
        } else {
          return "--";
        }
      } else {
        return "--";
      }
    } else {
      return "";
    }
  }

  public getOverdueDaysText(row: DeliveryTrackingRow) {
    if (row.status.name !== "COMPLETED") {
      if (row.leadTime) {
        if (row.firstOrderDate) {
          if (row.shipmentDate) {
            return row.overdueDays != null
                ? row.overdueDays >= 0
                    ? row.overdueDays : ""
                : "error";
          } else {
            return "--";
          }
        } else {
          return "--";
        }
      } else {
        return "--";
      }
    } else {
      if (row.leadTime) {
        if (row.firstOrderDate) {
          if (row.shipmentDate) {
            return row.overdueDays != null
                ? row.overdueDays > 0
                    ? row.overdueDays : ""
                : "noDate";
          } else {
            return "error";
          }
        } else {
          return "error";
        }
      } else {
        return "error";
      }
    }
  }

  public remainingDayClass(row: DeliveryTrackingRow): string {
    const days = this.getRemainingDaysText(row);
    if (days >= 0 && days <= 10) {
      return "text-warning";
    }
    return "";
  }

  public overdueDayClass(row: DeliveryTrackingRow): string {
    const overdueDays = this.getOverdueDaysText(row);
    if (row.status.name !== "COMPLETE" && overdueDays > 0) {
      return "text-danger";
    }
    if (row.status.name === "COMPLETE" && overdueDays > 0) {
      return "text-muted";
    }
    return "";
  }

  public isNearlyDue(row: DeliveryTrackingRow): boolean {
    const days = this.getRemainingDaysText(row);
    return days >= 0 && days <= 10;
  }

  public getRowClassName({ row }: { row: DeliveryTrackingRow }): string {
    if (this.isOverdue(row)) {
      return "row-danger";
    }
    if (this.isNearlyDue(row)) {
      return "row-warning";
    }
    return "";
  }

  public openPoDeliveryTracking(row: DeliveryTrackingRow): void {
    (this.$refs.poDeliveryTracking as PoDeliveryTracking).open(row, this.activeCustomer);
  }
}
